import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../../firebase';
import { portvilleBillingService } from '../../services/PortvilleBillingService';
import {
  PortvilleBill,
  PortvilleBillingSettings,
  PortvilleUsageRecord,
  PortvilleBuildingUsageRecord,
  PortvilleBillingCalculation,
  BillingSettingsFormData,
  BillGenerationRequest,
  UsageRecordFormData,
  BuildingUsageRecordFormData,
  BillingStatistics,
  BillingError,
  RoomBillingCalculation,
  BillingViewData
} from '../../types/portvilleBilling';

// Enhanced error handling interfaces
interface ErrorState {
  type: 'network' | 'permission' | 'validation' | 'unknown';
  message: string;
  details?: string;
  retryCount: number;
}

// Utility function to convert ErrorState to string for compatibility
const errorToString = (error: ErrorState | null): string | null => {
  return error ? error.message : null;
};

interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
}

interface UsePortvilleBillingReturn {
  // 데이터
  bills: PortvilleBill[];
  allBills: PortvilleBill[];
  billingSettings: PortvilleBillingSettings[];
  usageRecords: PortvilleUsageRecord[];
  buildingUsageRecords: PortvilleBuildingUsageRecord[];
  roomBillingCalculations: RoomBillingCalculation[];
  savedBillingCalculations: PortvilleBillingCalculation[];
  statistics: BillingStatistics | null;

  // 필터링 상태
  statusFilter: 'all' | 'pending' | 'paid' | 'overdue' | 'cancelled';
  searchTerm: string;

  // 로딩/에러 상태
  loading: boolean;
  connected: boolean;
  error: string | null; // Backward compatibility
  errorState: ErrorState | null; // Enhanced error state

  // 액션 함수들
  setStatusFilter: (status: 'all' | 'pending' | 'paid' | 'overdue' | 'cancelled') => void;
  setSearchTerm: (term: string) => void;
  saveBillingSettings: (formData: BillingSettingsFormData, createdBy: string) => Promise<boolean>;
  generateBills: (request: BillGenerationRequest) => Promise<boolean>;
  saveUsageRecords: (records: UsageRecordFormData[], recordedBy: string) => Promise<boolean>;
  saveUsageRecord: (record: UsageRecordFormData, recordedBy: string) => Promise<boolean>;
  deleteUsageRecord: (recordId: string) => Promise<void>;
  saveBuildingUsageRecord: (formData: BuildingUsageRecordFormData, recordedBy: string) => Promise<boolean>;
  deleteBuildingUsageRecord: (recordId: string) => Promise<void>;
  calculateRoomBilling: (year: number, month: number) => Promise<void>;
  saveBillingCalculations: (year: number, month: number, buildingUsageRecordId: string) => Promise<boolean>;
  loadSavedBillingCalculations: (year: number, month: number) => Promise<void>;
  deleteBillingCalculations: (year: number, month: number) => Promise<void>;
  applyCalculationsToBills: (year: number, month: number) => Promise<string[]>;
  updateBillStatus: (billId: string, status: PortvilleBill['status'], paidDate?: Date) => Promise<boolean>;
  updateBillPenalty: (billId: string, penaltyAmount: number) => Promise<boolean>;
  deleteBill: (billId: string) => Promise<void>;
  refreshData: () => void;
  loadStatistics: () => Promise<void>;
  retryLastOperation: () => Promise<void>;
}

// Utility functions for data transformation and validation
const transformFirestoreTimestamp = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  return timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
};

const validateBillData = (data: any): boolean => {
  return !!(data.roomNumber && data.billingPeriod && data.totalAmount !== undefined);
};

const classifyError = (error: any): ErrorState['type'] => {
  if (error?.code === 'permission-denied') return 'permission';
  if (error?.code === 'network-error' || error?.message?.includes('network')) return 'network';
  if (error?.code === 'invalid-argument') return 'validation';
  return 'unknown';
};

// Custom hook for retry logic with exponential backoff
const useRetry = (config: RetryConfig = { maxRetries: 3, baseDelay: 1000, maxDelay: 10000 }) => {
  const [retryState, setRetryState] = useState<{ count: number; isRetrying: boolean }>({
    count: 0,
    isRetrying: false
  });

  const calculateDelay = useCallback((attempt: number): number => {
    const delay = config.baseDelay * Math.pow(2, attempt);
    return Math.min(delay, config.maxDelay);
  }, [config.baseDelay, config.maxDelay]);

  const executeWithRetry = useCallback(async <T>(
    operation: () => Promise<T>,
    context: string = 'operation'
  ): Promise<T> => {
    let lastError: any;

    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          setRetryState({ count: attempt, isRetrying: true });
          const delay = calculateDelay(attempt - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        const result = await operation();
        setRetryState({ count: 0, isRetrying: false });
        return result;
      } catch (error) {
        console.warn(`${context} attempt ${attempt + 1} failed:`, error);
        lastError = error;

        if (attempt === config.maxRetries) {
          setRetryState({ count: 0, isRetrying: false });
          throw error;
        }
      }
    }

    throw lastError;
  }, [config.maxRetries, calculateDelay]);

  return { ...retryState, executeWithRetry };
};

export const usePortvilleBilling = (selectedYear?: number, selectedMonth?: number): UsePortvilleBillingReturn => {
  // Core state management
  const [allBills, setAllBills] = useState<PortvilleBill[]>([]);
  const [bills, setBills] = useState<PortvilleBill[]>([]);
  const [billingSettings, setBillingSettings] = useState<PortvilleBillingSettings[]>([]);
  const [usageRecords, setUsageRecords] = useState<PortvilleUsageRecord[]>([]);
  const [buildingUsageRecords, setBuildingUsageRecords] = useState<PortvilleBuildingUsageRecord[]>([]);
  const [roomBillingCalculations, setRoomBillingCalculations] = useState<RoomBillingCalculation[]>([]);
  const [savedBillingCalculations, setSavedBillingCalculations] = useState<PortvilleBillingCalculation[]>([]);
  const [statistics, setStatistics] = useState<BillingStatistics | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [errorState, setErrorState] = useState<ErrorState | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'paid' | 'overdue' | 'cancelled'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Subscription management
  const subscriptionsRef = useRef<{
    bills: Unsubscribe | null;
    settings: Unsubscribe | null;
    usage: Unsubscribe | null;
    building: Unsubscribe | null;
  }>({
    bills: null,
    settings: null,
    usage: null,
    building: null
  });

  // Current period state
  const currentYear = selectedYear ?? new Date().getFullYear();
  const currentMonth = selectedMonth ?? new Date().getMonth() + 1;

  // Retry functionality
  const { executeWithRetry, isRetrying } = useRetry();
  const lastOperationRef = useRef<(() => Promise<any>) | null>(null);

  // Memoized sorting functions
  const sortBillsByPeriod = useCallback((bills: PortvilleBill[]): PortvilleBill[] => {
    return [...bills].sort((a, b) => {
      if (a.billingPeriod.year !== b.billingPeriod.year) {
        return b.billingPeriod.year - a.billingPeriod.year;
      }
      if (a.billingPeriod.month !== b.billingPeriod.month) {
        return b.billingPeriod.month - a.billingPeriod.month;
      }
      return a.roomNumber.localeCompare(b.roomNumber);
    });
  }, []);

  const sortSettingsByRoom = useCallback((settings: PortvilleBillingSettings[]): PortvilleBillingSettings[] => {
    return [...settings].sort((a, b) => a.roomNumber.localeCompare(b.roomNumber));
  }, []);

  const sortUsageRecordsByRoom = useCallback((records: PortvilleUsageRecord[]): PortvilleUsageRecord[] => {
    return [...records].sort((a, b) => a.roomNumber.localeCompare(b.roomNumber));
  }, []);

  const sortBuildingUsageRecords = useCallback((records: PortvilleBuildingUsageRecord[]): PortvilleBuildingUsageRecord[] => {
    return [...records].sort((a, b) => b.recordedAt.getTime() - a.recordedAt.getTime());
  }, []);

  // Enhanced error handling
  const handleError = useCallback((error: any, context: string, retryCount: number = 0) => {
    const errorType = classifyError(error);
    const errorState: ErrorState = {
      type: errorType,
      message: error instanceof BillingError ? error.message : `${context}에 실패했습니다.`,
      details: error?.message,
      retryCount
    };

    console.error(`${context} 실패:`, error);
    setErrorState(errorState);
    setLoading(false);
    setConnected(errorType !== 'network');
  }, []);

  // Clear error with auto-clear timer
  const clearError = useCallback(() => {
    setErrorState(null);
  }, []);

  useEffect(() => {
    if (errorState) {
      const timer = setTimeout(clearError, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorState, clearError]);

  // Optimized subscription functions
  const subscribeToBills = useCallback((year: number, month: number) => {
    const subscribeOperation = async () => {
      setLoading(true);
      setErrorState(null);

      // Clear existing subscription
      if (subscriptionsRef.current.bills) {
        subscriptionsRef.current.bills();
      }

      const billsQuery = query(
        collection(db, 'portville_bills'),
        where('billingPeriod.year', '==', year),
        where('billingPeriod.month', '==', month)
      );

      return new Promise<void>((resolve, reject) => {
        const unsubscribe = onSnapshot(
          billsQuery,
          (snapshot) => {
            try {

              const billsData = snapshot.docs
                .map(doc => {
                  const data = doc.data();

                  if (!validateBillData(data)) {
                    console.warn(`Invalid bill data for document ${doc.id}:`, data);
                    return null;
                  }

                  const bill: PortvilleBill = {
                    id: doc.id,
                    ...data as any,
                    billingPeriod: {
                      ...data.billingPeriod,
                      startDate: transformFirestoreTimestamp(data.billingPeriod?.startDate),
                      endDate: transformFirestoreTimestamp(data.billingPeriod?.endDate)
                    },
                    issuedDate: transformFirestoreTimestamp(data.issuedDate),
                    dueDate: transformFirestoreTimestamp(data.dueDate),
                    paidDate: data.paidDate ? transformFirestoreTimestamp(data.paidDate) : undefined,
                    createdAt: transformFirestoreTimestamp(data.createdAt),
                    updatedAt: transformFirestoreTimestamp(data.updatedAt)
                  };

                  return bill;
                })
                .filter((bill): bill is PortvilleBill => bill !== null);

              const sortedBills = sortBillsByPeriod(billsData);


              setAllBills(sortedBills);
              setBills(sortedBills);
              setLoading(false);
              setConnected(true);
              resolve();
            } catch (error) {
              console.error('Bills snapshot processing error:', error);
              reject(error);
            }
          },
          (error) => {
            console.error('Bills subscription error:', error);
            reject(error);
          }
        );

        subscriptionsRef.current.bills = unsubscribe;
      });
    };

    executeWithRetry(() => subscribeOperation(), 'Bills subscription')
      .catch(error => {
        handleError(error, '청구서 데이터 로딩', 0);
        setAllBills([]);
        setBills([]);
      });
  }, [executeWithRetry, handleError, sortBillsByPeriod]);

  const subscribeToSettings = useCallback(() => {
    const subscribeOperation = async () => {
      // Clear existing subscription
      if (subscriptionsRef.current.settings) {
        subscriptionsRef.current.settings();
      }

      const settingsQuery = collection(db, 'portville_billing_settings');

      return new Promise<void>((resolve, reject) => {
        const unsubscribe = onSnapshot(
          settingsQuery,
          (snapshot) => {
            try {
              const settingsData = snapshot.docs
                .map(doc => {
                  const data = doc.data();
                  return {
                    id: doc.id,
                    ...data as any,
                    effectiveFrom: transformFirestoreTimestamp(data.effectiveFrom),
                    effectiveTo: data.effectiveTo ? transformFirestoreTimestamp(data.effectiveTo) : undefined,
                    createdAt: transformFirestoreTimestamp(data.createdAt),
                    updatedAt: transformFirestoreTimestamp(data.updatedAt)
                  } as PortvilleBillingSettings;
                });

              const sortedSettings = sortSettingsByRoom(settingsData);
              setBillingSettings(sortedSettings);
              resolve();
            } catch (error) {
              console.error('Settings snapshot processing error:', error);
              reject(error);
            }
          },
          (error) => {
            console.error('Settings subscription error:', error);
            reject(error);
          }
        );

        subscriptionsRef.current.settings = unsubscribe;
      });
    };

    executeWithRetry(() => subscribeOperation(), 'Settings subscription')
      .catch(error => {
        handleError(error, '관리비 설정 로딩', 0);
        setBillingSettings([]);
      });
  }, [executeWithRetry, handleError, sortSettingsByRoom]);

  const subscribeToUsageRecords = useCallback(() => {
    const subscribeOperation = async () => {
      // Clear existing subscription
      if (subscriptionsRef.current.usage) {
        subscriptionsRef.current.usage();
      }

      const usageQuery = collection(db, 'portville_usage_records');

      return new Promise<void>((resolve, reject) => {
        const unsubscribe = onSnapshot(
          usageQuery,
          (snapshot) => {
            try {
              const usageData = snapshot.docs
                .map(doc => {
                  const data = doc.data();
                  return {
                    id: doc.id,
                    ...data as any,
                    recordedAt: transformFirestoreTimestamp(data.recordedAt),
                    createdAt: transformFirestoreTimestamp(data.createdAt),
                    updatedAt: transformFirestoreTimestamp(data.updatedAt)
                  } as PortvilleUsageRecord;
                })
                .filter(record =>
                  record.period?.year === currentYear && record.period?.month === currentMonth
                );

              const sortedUsage = sortUsageRecordsByRoom(usageData);
              setUsageRecords(sortedUsage);
              resolve();
            } catch (error) {
              console.error('Usage records snapshot processing error:', error);
              reject(error);
            }
          },
          (error) => {
            console.error('Usage records subscription error:', error);
            reject(error);
          }
        );

        subscriptionsRef.current.usage = unsubscribe;
      });
    };

    executeWithRetry(() => subscribeOperation(), 'Usage records subscription')
      .catch(error => {
        handleError(error, '사용량 기록 로딩', 0);
        setUsageRecords([]);
      });
  }, [currentYear, currentMonth, executeWithRetry, handleError, sortUsageRecordsByRoom]);

  const subscribeToBuildingUsageRecords = useCallback(() => {
    const subscribeOperation = async () => {
      // Clear existing subscription
      if (subscriptionsRef.current.building) {
        subscriptionsRef.current.building();
      }

      const buildingUsageQuery = collection(db, 'portville_building_usage_records');

      return new Promise<Unsubscribe | null>((resolve, reject) => {
        const unsubscribe = onSnapshot(
          buildingUsageQuery,
          (snapshot) => {
            try {
              const buildingUsageData = snapshot.docs
                .map(doc => {
                  const data = doc.data();
                  return {
                    id: doc.id,
                    ...data as any,
                    recordedAt: transformFirestoreTimestamp(data.recordedAt),
                    createdAt: transformFirestoreTimestamp(data.createdAt),
                    updatedAt: transformFirestoreTimestamp(data.updatedAt)
                  } as PortvilleBuildingUsageRecord;
                })
                .filter(record =>
                  record.period?.year === currentYear && record.period?.month === currentMonth
                );

              const sortedBuilding = sortBuildingUsageRecords(buildingUsageData);
              setBuildingUsageRecords(sortedBuilding);
              resolve(unsubscribe);
            } catch (error) {
              console.error('Building usage records snapshot processing error:', error);
              reject(error);
            }
          },
          (error) => {
            console.error('Building usage records subscription error:', error);
            reject(error);
          }
        );

        subscriptionsRef.current.building = unsubscribe;
      });
    };

    executeWithRetry(() => subscribeOperation(), 'Building usage records subscription')
      .catch(error => {
        handleError(error, '건물 전체 사용량 로딩', 0);
        setBuildingUsageRecords([]);
        return null;
      });
  }, [currentYear, currentMonth, executeWithRetry, handleError, sortBuildingUsageRecords]);

  // Enhanced operation functions with better error handling
  const createOperationWrapper = useCallback(<T extends any[], R>(
    operation: (...args: T) => Promise<R>,
    operationName: string
  ) => {
    return async (...args: T): Promise<R> => {
      const wrappedOperation = () => operation(...args);
      lastOperationRef.current = wrappedOperation;

      try {
        return await executeWithRetry(wrappedOperation, operationName);
      } catch (error) {
        handleError(error, operationName, 0);
        throw error;
      }
    };
  }, [executeWithRetry, handleError]);

  // Operation functions
  const saveBillingSettings = useCallback(createOperationWrapper(async (
    formData: BillingSettingsFormData,
    createdBy: string
  ): Promise<boolean> => {
    await portvilleBillingService.saveBillingSettings(formData, createdBy);
    return true;
  }, '관리비 설정 저장'), [createOperationWrapper]);

  const generateBills = useCallback(createOperationWrapper(async (
    request: BillGenerationRequest
  ): Promise<boolean> => {
    setLoading(true);
    const billIds = await portvilleBillingService.generateBills(request);

    if (billIds.length === 0) {
      throw new Error('생성된 청구서가 없습니다. 설정을 확인해 주세요.');
    }

    setLoading(false);
    return true;
  }, '청구서 생성'), [createOperationWrapper]);

  const saveUsageRecords = useCallback(createOperationWrapper(async (
    records: UsageRecordFormData[],
    recordedBy: string
  ): Promise<boolean> => {
    await portvilleBillingService.saveUsageRecords(records, recordedBy);
    return true;
  }, '사용량 기록 저장'), [createOperationWrapper]);

  const saveUsageRecord = useCallback(createOperationWrapper(async (
    record: UsageRecordFormData,
    recordedBy: string
  ): Promise<boolean> => {
    await portvilleBillingService.saveUsageRecords([record], recordedBy);
    return true;
  }, '사용량 기록 저장'), [createOperationWrapper]);

  const deleteUsageRecord = useCallback(createOperationWrapper(async (
    recordId: string
  ): Promise<void> => {
    await portvilleBillingService.deleteUsageRecord(recordId);
  }, '사용량 기록 삭제'), [createOperationWrapper]);

  const updateBillStatus = useCallback(createOperationWrapper(async (
    billId: string,
    status: PortvilleBill['status'],
    paidDate?: Date
  ): Promise<boolean> => {
    await portvilleBillingService.updateBillStatus(billId, status, paidDate);
    return true;
  }, '청구서 상태 업데이트'), [createOperationWrapper]);

  const updateBillPenalty = useCallback(createOperationWrapper(async (
    billId: string,
    penaltyAmount: number
  ): Promise<boolean> => {
    await portvilleBillingService.updateBillPenalty(billId, penaltyAmount);
    return true;
  }, '청구서 가산금 업데이트'), [createOperationWrapper]);

  const loadStatistics = useCallback(createOperationWrapper(async (): Promise<void> => {
    const stats = await portvilleBillingService.getBillingStatistics(currentYear, currentMonth);
    setStatistics(stats);
  }, '통계 로드'), [createOperationWrapper, currentYear, currentMonth]);

  const saveBuildingUsageRecord = useCallback(createOperationWrapper(async (
    formData: BuildingUsageRecordFormData,
    recordedBy: string
  ): Promise<boolean> => {
    setLoading(true);

    const recordId = await portvilleBillingService.saveBuildingUsageRecord(formData, recordedBy);

    if (!recordId) {
      throw new Error('건물 전체 사용량 저장에 실패했습니다.');
    }

    setLoading(false);
    return true;
  }, '건물 전체 사용량 저장'), [createOperationWrapper]);

  const deleteBuildingUsageRecord = useCallback(createOperationWrapper(async (
    recordId: string
  ): Promise<void> => {
    setLoading(true);
    await portvilleBillingService.deleteBuildingUsageRecord(recordId);
    setLoading(false);
  }, '건물 전체 사용량 삭제'), [createOperationWrapper]);

  const calculateRoomBilling = useCallback(createOperationWrapper(async (
    year: number,
    month: number
  ): Promise<void> => {
    setLoading(true);
    setRoomBillingCalculations([]);

    const buildingUsageRecord = buildingUsageRecords.find(record =>
      record.period.year === year && record.period.month === month
    );

    if (!buildingUsageRecord) {
      throw new Error('해당 월의 건물 전체 사용량 데이터가 없습니다.');
    }

    const roomUsageRecords = usageRecords.filter(record =>
      record.period.year === year && record.period.month === month
    );

    if (roomUsageRecords.length === 0) {
      throw new Error('해당 월의 호실별 사용량 데이터가 없습니다.');
    }

    const calculations = await portvilleBillingService.calculateRoomBilling(
      year,
      month,
      roomUsageRecords,
      buildingUsageRecord,
      billingSettings
    );

    setRoomBillingCalculations(calculations);
    setLoading(false);
  }, '호실별 요금 계산'), [createOperationWrapper, buildingUsageRecords, usageRecords, billingSettings]);

  const saveBillingCalculations = useCallback(createOperationWrapper(async (
    year: number,
    month: number,
    buildingUsageRecordId: string
  ): Promise<boolean> => {
    if (roomBillingCalculations.length === 0) {
      throw new Error('저장할 계산 결과가 없습니다.');
    }

    await portvilleBillingService.saveBillingCalculations(
      year,
      month,
      roomBillingCalculations,
      buildingUsageRecordId
    );

    await loadSavedBillingCalculations(year, month);
    return true;
  }, '계산 결과 저장'), [createOperationWrapper, roomBillingCalculations]);

  const loadSavedBillingCalculations = useCallback(createOperationWrapper(async (
    year: number,
    month: number
  ) => {
    const calculations = await portvilleBillingService.getBillingCalculations(year, month);
    setSavedBillingCalculations(calculations);

    if (calculations.length > 0) {
      const roomCalculations: RoomBillingCalculation[] = calculations.map(calc => ({
        roomId: calc.roomId,
        roomNumber: calc.roomNumber,
        residentName: calc.residentName,
        electricity: calc.electricity,
        water: calc.water,
        usage: calc.usage,
        ratios: {
          electricityAreaContribution: (calc.ratios as any).electricityAreaContribution || (calc.ratios as any).areaContribution || 0,
          waterAreaContribution: (calc.ratios as any).waterAreaContribution || (calc.ratios as any).areaContribution || 0,
          electricityIndividual: calc.ratios.electricityIndividual,
          waterIndividual: calc.ratios.waterIndividual
        }
      }));
      setRoomBillingCalculations(roomCalculations);
    } else {
      setRoomBillingCalculations([]);
    }
  }, '저장된 계산 결과 로드'), [createOperationWrapper]);

  const deleteBillingCalculations = useCallback(createOperationWrapper(async (
    year: number,
    month: number
  ) => {
    await portvilleBillingService.deleteBillingCalculations(year, month);
    setSavedBillingCalculations([]);
    setRoomBillingCalculations([]);
  }, '계산 결과 삭제'), [createOperationWrapper]);

  const applyCalculationsToBills = useCallback(createOperationWrapper(async (
    year: number,
    month: number
  ): Promise<string[]> => {
    const billIds = await portvilleBillingService.applyCalculationsToBills(year, month);
    return billIds;
  }, '계산 결과를 청구서에 반영'), [createOperationWrapper]);

  // Data refresh function
  const refreshData = useCallback(() => {
    if (currentYear && currentMonth) {
      subscribeToBills(currentYear, currentMonth);
    }
    subscribeToSettings();
    subscribeToUsageRecords();
  }, [currentYear, currentMonth, subscribeToBills, subscribeToSettings, subscribeToUsageRecords]);

  // Retry last operation
  const retryLastOperation = useCallback(async (): Promise<void> => {
    if (lastOperationRef.current) {
      try {
        await lastOperationRef.current();
      } catch (error) {
        console.error('Retry operation failed:', error);
      }
    }
  }, []);

  // Initial data loading and subscription setup
  useEffect(() => {
    if (currentYear && currentMonth) {
      subscribeToBills(currentYear, currentMonth);
    }
    subscribeToSettings();
    subscribeToUsageRecords();
    subscribeToBuildingUsageRecords();

    return () => {
      // Cleanup all subscriptions
      Object.values(subscriptionsRef.current).forEach(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, [currentYear, currentMonth, subscribeToBills, subscribeToSettings, subscribeToUsageRecords, subscribeToBuildingUsageRecords]);

  // Sync bills when allBills changes
  useEffect(() => {
    setBills(allBills);
  }, [allBills]);

  // Update subscriptions when year/month changes
  useEffect(() => {
    if (currentYear && currentMonth) {
      subscribeToBills(currentYear, currentMonth);
      subscribeToUsageRecords();
    }
  }, [currentYear, currentMonth, subscribeToBills, subscribeToUsageRecords]);

  return {
    // 데이터
    bills,
    allBills,
    billingSettings,
    usageRecords,
    buildingUsageRecords,
    roomBillingCalculations,
    savedBillingCalculations,
    statistics,

    // 필터링 상태
    statusFilter,
    searchTerm,

    // 로딩/에러 상태
    loading: loading || isRetrying,
    connected,
    error: errorToString(errorState),
    errorState,

    // 액션 함수들
    setStatusFilter,
    setSearchTerm,
    saveBillingSettings,
    generateBills,
    saveUsageRecords,
    saveUsageRecord,
    deleteUsageRecord,
    saveBuildingUsageRecord,
    deleteBuildingUsageRecord,
    calculateRoomBilling,
    saveBillingCalculations,
    loadSavedBillingCalculations,
    deleteBillingCalculations,
    applyCalculationsToBills,
    updateBillStatus,
    updateBillPenalty,
    deleteBill: portvilleBillingService.deleteBill.bind(portvilleBillingService),
    refreshData,
    loadStatistics,
    retryLastOperation
  };
};

/**
 * 입주자용 관리비 조회 Hook
 */
interface UsePortvilleBillingViewReturn {
  billingData: BillingViewData | null;
  loading: boolean;
  error: string | null; // Backward compatibility
  errorState: ErrorState | null; // Enhanced error state
  loadBillingDataByUid: (uid: string) => Promise<boolean>;
  loadBillingDataByRoomNumber: (roomNumber: string) => Promise<boolean>;
}

export const usePortvilleBillingView = (): UsePortvilleBillingViewReturn => {
  const [billingData, setBillingData] = useState<BillingViewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState<ErrorState | null>(null);

  const { executeWithRetry } = useRetry();

  const handleError = useCallback((error: any, context: string, retryCount: number = 0) => {
    const errorType = classifyError(error);
    const errorState: ErrorState = {
      type: errorType,
      message: error instanceof BillingError ? error.message : `${context}에 실패했습니다.`,
      details: error?.message,
      retryCount
    };

    console.error(`${context} 실패:`, error);
    setErrorState(errorState);
    setLoading(false);
  }, []);

  const loadBillingDataByUid = useCallback(async (uid: string): Promise<boolean> => {
    const operation = async () => {
      setLoading(true);
      setErrorState(null);

      const data = await portvilleBillingService.getBillingDataByUid(uid);
      setBillingData(data);
      setLoading(false);
      return true;
    };

    try {
      return await executeWithRetry(operation, 'UID 기반 관리비 조회');
    } catch (error) {
      handleError(error, 'UID 기반 관리비 조회', 0);
      return false;
    }
  }, [executeWithRetry, handleError]);

  const loadBillingDataByRoomNumber = useCallback(async (roomNumber: string): Promise<boolean> => {
    const operation = async () => {
      setLoading(true);
      setErrorState(null);

      const data = await portvilleBillingService.getBillingDataByRoomNumber(roomNumber);
      setBillingData(data);
      setLoading(false);
      return true;
    };

    try {
      return await executeWithRetry(operation, '호실번호 기반 관리비 조회');
    } catch (error) {
      handleError(error, '호실번호 기반 관리비 조회', 0);
      return false;
    }
  }, [executeWithRetry, handleError]);

  return {
    billingData,
    loading,
    error: errorToString(errorState),
    errorState,
    loadBillingDataByUid,
    loadBillingDataByRoomNumber
  };
};

/**
 * SMS 인증 Hook
 */
interface UsePortvilleSmsAuthReturn {
  loading: boolean;
  error: string | null; // Backward compatibility
  errorState: ErrorState | null; // Enhanced error state
  isVerified: boolean;
  sessionToken: string | null;
  sendAuthCode: (roomNumber: string, phoneNumber: string) => Promise<boolean>;
  verifyAuthCode: (roomNumber: string, phoneNumber: string, code: string) => Promise<boolean>;
  clearAuth: () => void;
}

export const usePortvilleSmsAuth = (): UsePortvilleSmsAuthReturn => {
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState<ErrorState | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);

  const { executeWithRetry } = useRetry();

  const handleError = useCallback((error: any, context: string, retryCount: number = 0) => {
    const errorType = classifyError(error);
    const errorState: ErrorState = {
      type: errorType,
      message: error instanceof BillingError ? error.message : `${context}에 실패했습니다.`,
      details: error?.message,
      retryCount
    };

    console.error(`${context} 실패:`, error);
    setErrorState(errorState);
    setLoading(false);
  }, []);

  const sendAuthCode = useCallback(async (roomNumber: string, phoneNumber: string): Promise<boolean> => {
    const operation = async () => {
      setLoading(true);
      setErrorState(null);

      await portvilleBillingService.sendSmsAuthCode({ roomNumber, phoneNumber });
      setLoading(false);
      return true;
    };

    try {
      return await executeWithRetry(operation, 'SMS 인증코드 발송');
    } catch (error) {
      handleError(error, 'SMS 인증코드 발송', 0);
      return false;
    }
  }, [executeWithRetry, handleError]);

  const verifyAuthCode = useCallback(async (
    roomNumber: string,
    phoneNumber: string,
    code: string
  ): Promise<boolean> => {
    const operation = async () => {
      setLoading(true);
      setErrorState(null);

      const token = await portvilleBillingService.verifySmsAuthCode({
        roomNumber,
        phoneNumber,
        verificationCode: code
      });

      setSessionToken(token);
      setIsVerified(true);
      setLoading(false);
      return true;
    };

    try {
      return await executeWithRetry(operation, 'SMS 인증 확인');
    } catch (error) {
      handleError(error, 'SMS 인증 확인', 0);
      return false;
    }
  }, [executeWithRetry, handleError]);

  const clearAuth = useCallback(() => {
    setIsVerified(false);
    setSessionToken(null);
    setErrorState(null);
  }, []);

  return {
    loading,
    error: errorToString(errorState),
    errorState,
    isVerified,
    sessionToken,
    sendAuthCode,
    verifyAuthCode,
    clearAuth
  };
};